import { useEffect, useRef } from 'react';

interface HeroSectionProps {
  onCTA: () => void;
}

export default function HeroSection({ onCTA }: HeroSectionProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) el.classList.add('opacity-100', 'translate-y-0');
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      style={{ background: '#0a0e1a' }}
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/00d190dc9222fb6afd6198d0ed2cb694.jpg"
          alt="Concrete structure background"
          className="w-full h-full object-cover object-top opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0e1a]/70 via-[#0a0e1a]/50 to-[#0a0e1a]/90"></div>
        {/* Concrete texture overlay */}
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,255,255,0.03) 2px, rgba(255,255,255,0.03) 4px)',
        }}></div>
      </div>

      <div
        ref={ref}
        className="relative z-10 w-full max-w-5xl mx-auto px-4 md:px-8 text-center opacity-0 translate-y-8 transition-all duration-1000 ease-out pt-24 pb-16"
      >
        {/* Badge */}
        <div className="inline-flex items-center gap-2 bg-[#0055ff]/20 border border-[#0055ff]/40 text-[#4d8fff] text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-full mb-8">
          <span className="w-4 h-4 flex items-center justify-center"><i className="ri-shield-check-fill"></i></span>
          <span className="hidden sm:inline">The Concrete Leak Specialist — South Florida</span>
          <span className="sm:hidden">Concrete Leak Specialist</span>
        </div>

        <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-white leading-tight mb-6 tracking-tight">
          Permanent Water Intrusion Solutions
          <span className="text-[#0055ff]"> for High-Risk Concrete Structures</span>
        </h1>

        <p className="text-base md:text-xl text-gray-300 max-w-3xl mx-auto mb-10 leading-relaxed">
          Engineered injection technology to stop water intrusion at its source —
          even under hydrostatic pressure.
        </p>

        {/* Trust badges */}
        <div className="flex flex-wrap justify-center gap-3 md:gap-6 mb-10">
          {[
            { icon: 'ri-settings-3-fill', label: 'Engineer-Approved' },
            { icon: 'ri-shield-fill', label: '5-Year Warranty' },
            { icon: 'ri-building-2-fill', label: 'No Demolition' },
            { icon: 'ri-checkbox-circle-fill', label: 'Since 2017' },
          ].map((b) => (
            <div key={b.label} className="flex items-center gap-2 bg-white/5 border border-white/10 px-3 py-2 rounded-full text-xs sm:text-sm text-white font-medium">
              <span className="w-4 h-4 flex items-center justify-center text-[#0055ff]">
                <i className={b.icon}></i>
              </span>
              {b.label}
            </div>
          ))}
        </div>

        {/* CTAs */}
        <div className="flex flex-col gap-3 sm:gap-4 justify-center mb-12 max-w-lg mx-auto sm:max-w-none">
          <button
            onClick={onCTA}
            className="bg-[#0055ff] hover:bg-[#0044cc] text-white font-bold px-6 py-4 rounded-md text-sm uppercase tracking-wider transition-all duration-200 whitespace-normal sm:whitespace-nowrap shadow-lg shadow-[#0055ff]/30 w-full sm:w-auto"
          >
            Schedule a Free Engineering Inspection
          </button>
          <button
            onClick={onCTA}
            className="bg-transparent border-2 border-white/40 hover:border-white text-white font-bold px-6 py-4 rounded-md text-sm uppercase tracking-wider transition-all duration-200 whitespace-normal sm:whitespace-nowrap w-full sm:w-auto"
          >
            Request a Professional Water Intrusion Assessment
          </button>
        </div>

        {/* Bold statements */}
        <div className="flex flex-col sm:flex-row justify-center items-center gap-2 sm:gap-8">
          {['NO OBLIGATION.', 'NO GUESSWORK.', 'JUST TECHNICAL CLARITY.'].map((s, i) => (
            <span key={i} className="text-white font-black text-xs sm:text-sm md:text-base tracking-widest uppercase">
              {s}
            </span>
          ))}
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/40 animate-bounce">
        <span className="text-xs uppercase tracking-widest">Scroll</span>
        <span className="w-5 h-5 flex items-center justify-center">
          <i className="ri-arrow-down-line text-lg"></i>
        </span>
      </div>
    </section>
  );
}
